"use client";

import { useEffect, useRef, useState } from "react";
import { T } from "@/lib/i18n/T";
import { DatePicker } from "./DatePicker";
import "./myinfo.css";

/* ----------------------------------------------------------------- types
   Shapes mirror what the backend/account API will return, so wiring later is
   a drop-in: replace the local state seeds with the fetched profile and the
   `persist()` stub with the real mutation. */
export type Address = {
  id: string;
  label: string;
  recipient: string;
  street: string;
  city: string;
  postal: string;
  country: string;
  isDefault: boolean;
};

export type Profile = {
  fullName: string;
  dob: string | null; // ISO yyyy-mm-dd
  email: string;
  dialCode: string;
  phone: string;
  photo: string | null; // object URL (local) → becomes a CDN URL once uploaded
  addresses: Address[];
};

const DIAL_CODES = ["+39", "+7", "+1", "+44", "+34", "+49", "+33", "+81", "+82", "+61", "+55", "+90"];
const COUNTRIES = [
  "Italy", "Russia", "United States", "United Kingdom", "Spain", "Germany",
  "France", "Japan", "South Korea", "Australia", "Brazil", "Türkiye",
];

/** Empty profile — the seed until the authenticated session is wired in. */
const EMPTY_PROFILE: Profile = {
  fullName: "",
  dob: null,
  email: "",
  dialCode: "+39",
  phone: "",
  photo: null,
  addresses: [],
};

const emptyAddress = (id: string): Address => ({
  id,
  label: "Home",
  recipient: "",
  street: "",
  city: "",
  postal: "",
  country: "Italy",
  isDefault: false,
});

export function MyInfo() {
  // TODO(backend): seed from the authenticated profile instead of EMPTY_PROFILE.
  const [profile, setProfile] = useState<Profile>(EMPTY_PROFILE);
  const [saved, setSaved] = useState(false);

  const idSeq = useRef(0);
  const photoUrl = useRef<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  // Revoke the last object URL on unmount to avoid leaking it.
  useEffect(() => () => { if (photoUrl.current) URL.revokeObjectURL(photoUrl.current); }, []);

  const set = <K extends keyof Profile>(key: K, val: Profile[K]) => {
    setProfile((p) => ({ ...p, [key]: val }));
    setSaved(false);
  };

  const onPhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (photoUrl.current) URL.revokeObjectURL(photoUrl.current);
    const url = URL.createObjectURL(file);
    photoUrl.current = url;
    set("photo", url);
  };

  const removePhoto = () => {
    if (photoUrl.current) URL.revokeObjectURL(photoUrl.current);
    photoUrl.current = null;
    set("photo", null);
  };

  /* ---- addresses ---- */
  const addAddress = () => {
    idSeq.current += 1;
    const addr = emptyAddress(`a${idSeq.current}`);
    if (profile.addresses.length === 0) addr.isDefault = true;
    set("addresses", [...profile.addresses, addr]);
  };
  const updateAddress = (id: string, patch: Partial<Address>) =>
    set("addresses", profile.addresses.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  const removeAddress = (id: string) => {
    const next = profile.addresses.filter((a) => a.id !== id);
    if (next.length && !next.some((a) => a.isDefault)) next[0].isDefault = true;
    set("addresses", next);
  };
  const makeDefault = (id: string) =>
    set("addresses", profile.addresses.map((a) => ({ ...a, isDefault: a.id === id })));

  const persist = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO(backend): PATCH /account/profile with `profile`.
    setSaved(true);
  };

  return (
    <form className="rb-myinfo csx3-C00 csx3-w-full" onSubmit={persist}>
      {/* ---- Profile photo ---- */}
      <section className="rb-myinfo-photo csx3-R01 csx3-g-16">
        <div className="rb-myinfo-avatar csx3-R12">
          {profile.photo ? (
            // eslint-disable-next-line @next/next/no-img-element -- local blob preview, not an optimizable asset
            <img src={profile.photo} alt="Profile" className="csx3-w-full csx3-h-full" />
          ) : (
            // Same "omino" silhouette as the navbar avatar, anchored to the bottom.
            <svg className="rb-myinfo-avatar-ico" width="46" height="50" viewBox="0 0 22 24" fill="currentColor" aria-hidden="true">
              <path d="M10.9968 11.517C14.1772 11.517 16.7553 8.93887 16.7553 5.75852C16.7553 2.57817 14.1772 0 10.9968 0C7.81645 0 5.23828 2.57817 5.23828 5.75852C5.23828 8.93887 7.81645 11.517 10.9968 11.517Z" />
              <path d="M21.947 23.0341C21.203 20.7144 19.7418 18.6909 17.774 17.2554C15.8059 15.8198 13.4329 15.0463 10.9969 15.0463C8.56097 15.0463 6.18794 15.8198 4.21996 17.2554C2.252 18.6909 0.790738 20.7144 0.046875 23.0341L0.0515282 24H21.9517L21.947 23.0341Z" />
            </svg>
          )}
        </div>
        <div className="csx3-C00 csx3-g-6">
          <span className="rb-myinfo-photo-label"><T id="dashboard.info.photo" /></span>
          <div className="csx3-R01 csx3-g-8">
            <button type="button" className="rb-btn rb-btn--ghost" onClick={() => fileInput.current?.click()}>
              <T id="dashboard.info.upload" />
            </button>
            {profile.photo && (
              <button type="button" className="rb-link-btn rb-link-btn--danger" onClick={removePhoto}>
                <T id="common.remove" />
              </button>
            )}
          </div>
          <input ref={fileInput} type="file" accept="image/*" hidden onChange={onPhoto} />
        </div>
      </section>

      {/* ---- Identity fields ---- */}
      <div className="rb-myinfo-grid">
        <label className="rb-field csx3-C00 csx3-g-6">
          <span className="rb-field-label"><T id="dashboard.info.fullName" /></span>
          <input
            className="rb-field-control"
            type="text"
            autoComplete="name"
            placeholder="Jane Doe"
            value={profile.fullName}
            onChange={(e) => set("fullName", e.target.value)}
          />
        </label>

        <div className="rb-field csx3-C00 csx3-g-6">
          <span className="rb-field-label"><T id="dashboard.info.birthDate" /></span>
          <DatePicker value={profile.dob} onChange={(iso) => set("dob", iso)} placeholder="Select your birthday" />
        </div>

        <label className="rb-field csx3-C00 csx3-g-6">
          <span className="rb-field-label"><T id="common.email" /></span>
          <input
            className="rb-field-control"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={profile.email}
            onChange={(e) => set("email", e.target.value)}
          />
        </label>

        <div className="rb-field csx3-C00 csx3-g-6">
          <span className="rb-field-label"><T id="dashboard.info.phone" /></span>
          <div className="rb-phone csx3-R01 csx3-g-8">
            <select
              className="rb-field-control rb-phone-code"
              value={profile.dialCode}
              onChange={(e) => set("dialCode", e.target.value)}
              aria-label="Dial code"
            >
              {DIAL_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input
              className="rb-field-control csx3-f1"
              type="tel"
              autoComplete="tel-national"
              placeholder="123 456 7890"
              value={profile.phone}
              onChange={(e) => set("phone", e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* ---- Addresses ---- */}
      <section className="rb-myinfo-addresses csx3-C00 csx3-w-full csx3-g-12">
        <div className="rb-myinfo-addresses-head csx3-Rs1 csx3-w-full">
          <h3 className="rb-myinfo-subtitle"><T id="dashboard.info.addresses" /></h3>
          <button type="button" className="rb-btn rb-btn--ghost" onClick={addAddress}>
            <T id="dashboard.info.addAddress" />
          </button>
        </div>

        {profile.addresses.length === 0 ? (
          <p className="rb-myinfo-hint"><T id="dashboard.info.noAddresses" /></p>
        ) : (
          <div className="rb-myinfo-address-list csx3-C00 csx3-w-full csx3-g-12">
            {profile.addresses.map((a) => (
              <fieldset key={a.id} className={`rb-address csx3-C00 csx3-w-full csx3-g-12 ${a.isDefault ? "is-default" : ""}`}>
                <div className="rb-address-top csx3-Rs1 csx3-w-full">
                  <input
                    className="rb-field-control rb-address-label"
                    type="text"
                    placeholder="Label (Home, Work…)"
                    value={a.label}
                    onChange={(e) => updateAddress(a.id, { label: e.target.value })}
                  />
                  {a.isDefault
                    ? <span className="rb-address-default-tag"><T id="dashboard.info.default" /></span>
                    : <button type="button" className="rb-link-btn" onClick={() => makeDefault(a.id)}><T id="dashboard.info.setDefault" /></button>}
                </div>

                <div className="rb-address-grid">
                  <input className="rb-field-control" type="text" autoComplete="name" placeholder="Recipient" value={a.recipient} onChange={(e) => updateAddress(a.id, { recipient: e.target.value })} />
                  <input className="rb-field-control" type="text" autoComplete="street-address" placeholder="Street and number" value={a.street} onChange={(e) => updateAddress(a.id, { street: e.target.value })} />
                  <input className="rb-field-control" type="text" autoComplete="address-level2" placeholder="City" value={a.city} onChange={(e) => updateAddress(a.id, { city: e.target.value })} />
                  <input className="rb-field-control" type="text" autoComplete="postal-code" placeholder="Postal code" value={a.postal} onChange={(e) => updateAddress(a.id, { postal: e.target.value })} />
                  <select className="rb-field-control" value={a.country} onChange={(e) => updateAddress(a.id, { country: e.target.value })} aria-label="Country">
                    {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                <div className="rb-address-actions csx3-R01 csx3-w-full">
                  <button type="button" className="rb-link-btn rb-link-btn--danger" onClick={() => removeAddress(a.id)}>
                    <T id="dashboard.info.removeAddress" />
                  </button>
                </div>
              </fieldset>
            ))}
          </div>
        )}
      </section>

      {/* ---- Save bar ---- */}
      <div className="rb-myinfo-savebar csx3-R01 csx3-g-12">
        <button type="submit" className="rb-btn rb-btn--primary"><T id="dashboard.info.save" /></button>
        {saved && <span className="rb-myinfo-saved csx3-R01 csx3-g-6" role="status">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
          <T id="dashboard.info.saved" />
        </span>}
      </div>
    </form>
  );
}
